import { useMemo, useState } from 'react';
import { TablePaginationConfig } from 'src/components/CustomPagination/paginationDefaults';
import type { PageState } from 'src/components/CustomPagination/usePersistedPageState';

interface UseClientPaginationParams<T> {
  /* CONSUMER NOTE — this is the source-of-truth array the consumer is paging
   * over (e.g. `stagedEntries` in StepReviewChanges). Whatever data structure
   * you manage upstream — Map, sorted array, filtered list — flatten it to an
   * ordered T[] before passing it in. The hook does not mutate or sort */
  items: T[];
  itemsPerPage: number;
  position?: TablePaginationConfig['position'];
  /* Optional external page state (e.g. a redux-backed `usePersistedPageState`).
   * Omit it and the hook owns page state locally (uncontrolled). Pass it and
   * the caller owns it, so the page survives unmount. */
  pageStateFromRedux?: PageState;
}

interface UseClientPaginationReturn<T> {
  currentPage: number;
  totalPages: number;
  paginatedItems: T[];
  showPaginationControls: boolean;
  showTopPagination: boolean;
  showBottomPagination: boolean;
  setCurrentPage: (page: number) => void;
  resetToFirstPage: () => void;
}

const FIRST_PAGE: number = 1;
const DEFAULT_POSITION: TablePaginationConfig['position'] = 'bottom';

/* Slim client-side pagination. Owns only page state — the consumer owns the
 * data. Returns the slice of `items` for the current page, plus controls. */
export const useClientPagination = <T,>({
  items,
  itemsPerPage,
  position = DEFAULT_POSITION,
  pageStateFromRedux,
}: UseClientPaginationParams<T>): UseClientPaginationReturn<T> => {
  const [localPage, setLocalPage] = useState<number>(FIRST_PAGE);

  const localPageState: PageState = {
    page: localPage,
    setPage: setLocalPage,
  };

  const activePageState: PageState = pageStateFromRedux ?? localPageState;

  const { page: requestedPage, setPage: setRequestedPage }: PageState =
    activePageState;

  const totalPages: number = useMemo(() => {
    if (items.length === 0 || itemsPerPage <= 0) {
      return 0;
    }

    return Math.ceil(items.length / itemsPerPage);
  }, [items.length, itemsPerPage]);

  const currentPage: number =
    totalPages > 0 ? Math.min(requestedPage, totalPages) : requestedPage;

  const paginatedItems: T[] = useMemo(() => {
    if (items.length === 0) {
      return [];
    }

    const pageIndex: number = currentPage - 1;
    const windowStart: number = pageIndex * itemsPerPage;
    const windowEnd: number = windowStart + itemsPerPage;

    return items.slice(windowStart, windowEnd);
  }, [items, currentPage, itemsPerPage]);

  const setCurrentPage = (page: number): void => {
    if (page < FIRST_PAGE || page > totalPages) {
      return;
    }

    setRequestedPage(page);
  };

  const resetToFirstPage = (): void => {
    setRequestedPage(FIRST_PAGE);
  };

  const showPaginationControls: boolean = totalPages > 1;

  const showAtTop: boolean = position === 'top' || position === 'both';
  const showAtBottom: boolean = position === 'bottom' || position === 'both';

  const showTopPagination: boolean = showPaginationControls && showAtTop;
  const showBottomPagination: boolean = showPaginationControls && showAtBottom;

  return {
    currentPage,
    totalPages,
    paginatedItems,
    showPaginationControls,
    showTopPagination,
    showBottomPagination,
    setCurrentPage,
    resetToFirstPage,
  };
};
